<template>
  <section class="basket-product">
    <a :href="result.detailPage" class="basket-product__picture" @click="handlerClick">
      <DefImg :result="picture" />
      <GiaPhoto v-if="result.isGia" />
    </a>
    <div class="basket-product__block">
      <div class="basket-product__main">
        <a :href="result.detailPage" class="basket-product__link" @click="handlerClick">
          <span class="basket-product__name">{{ result.type }}</span>
          <span v-if="result.collection" class="basket-product__collection">
            {{ result.collection.name }}
          </span>
        </a>
      </div>
      <div class="basket-product-controls">
        <DefSelectWrapper v-if="SIZES && getActiveSize">
          <template #header>
            {{ getActiveSize }}
          </template>
          <template #placeholder>
            {{ messages.size }}
          </template>
          <div
            v-for="(size, i) in SIZES"
            :key="i"
            :class="{ 'sku-disabled': size.disable }"
            @click="handlerChangeSKU({ id, size })"
          >
            {{ size.displayValue }}
          </div>
        </DefSelectWrapper>
        <DefCounter v-if="result.isShowCounter" :result="result" />
      </div>
      <div class="basket-product__bottom">
        <div class="basket-product__prices">
          <DefPriceOld v-if="price && price.printPriceOld">
            {{ price.printPriceOld }}
          </DefPriceOld>
          <DefPrice v-if="price && price.printPrice">
            {{ price.printPrice }}
          </DefPrice>
        </div>

        <DefSale v-if="price && price.printDiscountPercent">
          {{ price.printDiscountPercent }}
        </DefSale>
      </div>
      <span class="basket-product__remove" @click="remove">
        <svg><use xlink:href="#svg-trash"></use></svg>
      </span>
    </div>
    <BasketBoxView v-if="result?.boxes?.length" :result="result" />
    <BasketProductNotAvailable :result="result" />
    <ProductRengravingBanner />
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import DefImg from '@components/Def/DefImg/DefImg.vue';
import GiaPhoto from '@components/Product/GiaPhoto.vue';
import DefCounter from '@components/Def/DefCounter/DefCounter';
import DefSelectWrapper from '@components/Def/DefSelect/DefSelectWrapper.vue';
import DefPrice from '@components/Def/DefPrice/DefPrice.vue';
import DefPriceOld from '@components/Def/DefPriceOld/DefPriceOld.vue';
import DefSale from '@components/Def/DefSale/DefSale.vue';
import BasketBoxView from '@components/Basket/BasketBox/BasketBoxView.vue';

import Analytics from '@/utils/Analytics';
import BasketProductNotAvailable from '@components/Basket/BasketProduct/BasketProductNotAvailable.vue';
import ProductRengravingBanner from '@components/Product/ProductRengraving/ProductRengravingBanner.vue';

export default {
  name: 'BasketProduct',
  components: {
    ProductRengravingBanner,
    BasketProductNotAvailable,
    DefCounter,
    DefImg,
    GiaPhoto,
    DefSelectWrapper,
    DefPrice,
    DefPriceOld,
    DefSale,
    BasketBoxView,
  },
  props: ['result', 'change', 'orderNumber'],
  computed: {
    ...mapState({
      messages: ({ basket }) => basket.data?.messages,
    }),
    picture() {
      return this.result?.picture;
    },
    SIZES() {
      return this.result?.skuMatrix?.SIZES?.items;
    },
    price() {
      return this.result?.price;
    },
    getActiveSize() {
      return this.SIZES?.find((el) => el.active)?.displayValue;
    },
    id() {
      return this.result?.offer?.id ?? this.result?.id;
    },
  },
  methods: {
    ...mapActions({
      changeSKU: 'basket/changeSKU',
    }),
    async handlerChangeSKU(item) {
      if (this.getActiveSize == item.size.displayValue) return;

      const product = await this.changeSKU(item);
      this.change(this.orderNumber, product, item);

      Analytics.updatedBasket();
    },
    remove() {
      try {
        this.$emit('remove', this.result);
        // Analytics.removeFromBasket(this.result);
      } catch (err) {
        console.error(err);
      }
    },
    handlerClick() {
      Analytics.clickOnProduct(this.result);
    },
  },
};
</script>

<style lang="scss" scoped>
.basket-product {
  position: relative;
  display: grid;
  grid-template-columns: 50px 1fr;
  grid-gap: 12px;
  border-bottom: 1px solid var(--border-grey);
  padding-bottom: 16px;
  @include point-md {
    grid-template-columns: 60px 1fr;
    grid-gap: 8px 16px;
  }
  &__picture {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 80px;
    position: relative;
  }
  &__block {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-row-gap: 16px;
    grid-column-gap: 12px;
    align-items: center;
    @include point-md {
      grid-template-columns: 2fr 1fr 1fr 20px;
      grid-column-gap: 24px;
    }
    @include point-lg {
      grid-template-columns: 2fr 1fr 1.5fr 20px;
    }
  }
  &__main {
    grid-column-start: 1;
    grid-column-end: 3;
    @include point-md {
      grid-column-start: 1;
      grid-column-end: 2;
    }
  }
  &__main,
  &__link {
    display: flex;
    flex-direction: column;
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    @include point-md {
      align-items: flex-start;
    }
  }
  &__prices {
    display: flex;
    flex-direction: column;
    gap: 2px;
    @include point-lg {
      flex-direction: row;
      gap: 8px;
    }
  }
  &__remove {
    cursor: pointer;
    position: absolute;
    top: 17px;
    right: 0;
    @include point-md {
      position: static;
    }
    svg {
      display: block;
      width: 20px;
      height: 20px;
      fill: #030304;
      transition: 0.2s linear fill;
    }
    &:hover {
      svg {
        fill: var(--color-red);
      }
    }
  }
  &__name {
    font-size: 16px;
    font-weight: 500;
    line-height: 125%;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  &__collection {
    font-size: 12px;
    font-weight: 500;
    line-height: 125%;
    text-transform: uppercase;
    color: var(--color-dark-grey);
  }
  :deep(.def-price-old) {
    font-size: 14px;
    font-weight: 500;
    line-height: 125%;
    margin-bottom: 2px;
  }
  :deep(.def-price) {
    font-size: 16px;
    font-weight: 600;
    line-height: 125%;
  }
  :deep(.def-sale) {
    margin-top: 4px;
  }
  :deep(.gia-photo) {
    position: absolute;
    right: 0;
    bottom: 0;
    max-width: 32px;
  }
  :deep(.def-select-wrapper__placeholder) {
    display: none;
  }
  :deep(.def-select-wrapper__front) {
    min-height: 44px;
    justify-content: center;
  }
  :deep(.product-rengraving-banner-props) {
    flex-direction: row;
  }
}
.basket-product-controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-row-gap: 12px;
}
.sku-disabled {
  opacity: 0.6;
  pointer-events: none;
}
</style>
